/**
 * Utility functions for handling request numbers in the Thaklis system
 */

/**
 * Format an organization-specific request number with the organization prefix.
 * The format is: {prefix}-R-{requestNumber}
 * For example: ORG-R-23
 * 
 * @param {string} prefix The organization prefix (3 letters)
 * @param {number} requestNumber The request number
 * @return {string} The formatted request number string
 */
export const formatOrganizationRequestNumber = (prefix, requestNumber) => {
  if (!prefix || !requestNumber) {
    return 'Unknown';
  }
  return `${prefix}-R-${requestNumber}`;
};

/**
 * Format a system-wide request number.
 * The format is: SYS-{systemRequestNumber}
 * For example: SYS-10000046
 * 
 * @param {number} systemRequestNumber The system-wide request number
 * @return {string} The formatted system request number string
 */
export const formatSystemRequestNumber = (systemRequestNumber) => {
  if (!systemRequestNumber) {
    return 'Unknown';
  }
  return `SYS-${systemRequestNumber}`;
};

/**
 * Get a formatted request ID from a workflow request object
 * Uses organization prefix and request number
 * 
 * @param {Object} request The workflow request object
 * @return {string} The formatted request ID
 */
export const getFormattedRequestId = (request) => {
  if (!request) {
    return 'Unknown';
  }
  
  // Check for the new format first
  if (request.orgSequenceNumber) {
    return request.orgSequenceNumber;
  }
  
  // Fallback to old format
  if (request.organizationRequestPrefix && request.requestNumber) {
    return formatOrganizationRequestNumber(
      request.organizationRequestPrefix,
      request.requestNumber
    );
  }
  
  // Fallback to system request number if available
  if (request.systemRequestNumber) {
    return formatSystemRequestNumber(request.systemRequestNumber);
  }
  
  // Last resort, use the UUID
  return request.id || 'Unknown';
};
